import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { supabase } from '@/integrations/supabase/client';

interface ScoreRange {
  range: string;
  count: number;
  color: string;
}

export function ScoreDistributionChart() {
  const [data, setData] = useState<ScoreRange[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchScoreDistribution() {
      try {
        const { data: evaluations } = await supabase
          .from('simulation_evaluations')
          .select('overall_score')
          .not('overall_score', 'is', null)
          .eq('is_submitted', true);

        const ranges = [
          { range: '0-20', min: 0, max: 20, count: 0, color: 'hsl(var(--destructive))' },
          { range: '21-40', min: 21, max: 40, count: 0, color: 'hsl(0, 84%, 60%)' },
          { range: '41-60', min: 41, max: 60, count: 0, color: 'hsl(45, 93%, 47%)' },
          { range: '61-80', min: 61, max: 80, count: 0, color: 'hsl(142, 76%, 36%)' },
          { range: '81-100', min: 81, max: 100, count: 0, color: 'hsl(142, 71%, 45%)' },
        ];

        evaluations?.forEach(e => {
          const score = e.overall_score || 0;
          for (const range of ranges) {
            if (score >= range.min && score <= range.max) {
              range.count++;
              break;
            }
          }
        });

        setData(ranges.map(r => ({ range: r.range, count: r.count, color: r.color })));
      } catch (error) {
        console.error('Error fetching score distribution:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchScoreDistribution();
  }, []);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Score Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] animate-pulse bg-muted rounded-lg" />
        </CardContent>
      </Card>
    );
  }

  const totalSessions = data.reduce((sum, d) => sum + d.count, 0);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Score Distribution</CardTitle>
        <span className="text-sm text-muted-foreground">{totalSessions} total sessions</span>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
              <XAxis 
                dataKey="range" 
                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                axisLine={{ stroke: 'hsl(var(--border))' }}
              />
              <YAxis 
                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                axisLine={{ stroke: 'hsl(var(--border))' }}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'hsl(var(--card))', 
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px',
                  color: 'hsl(var(--foreground))'
                }}
                formatter={(value: number) => [`${value} sessions`, 'Count']}
              />
              <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="flex justify-center gap-4 mt-4 text-xs">
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-red-500" /> Needs Improvement
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-yellow-500" /> Average
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded bg-green-500" /> Excellent
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
